import { singletonGetter } from "./deprecated/singletonGetter";

/* eslint-disable @faire/one-exported-class-per-file */

const getLocalStorage = (): Window["localStorage"] | undefined => {
  try {
    if (typeof window === "undefined") {
      return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const getSessionStorage = (): Window["sessionStorage"] | undefined => {
  try {
    if (typeof window === "undefined") {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return window.sessionStorage;
  } catch {
    return undefined;
  }
};

export class Storage {
  static get local() {
    return LocalStorage.get();
  }

  static get session() {
    return SessionStorage.get();
  }

  protected _fallbackCache: Record<string, string> = {};

  // Cached lazily on first use rather than in the constructor: `StorageService`
  // is instantiated at module load time, too early to safely probe storage in
  // some contexts (e.g. SSR, or before `window` is ready in a test harness).
  private _isStorageAvailableCache: boolean | undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(public dataStorage?: any) {}

  isStorageAvailable = (): boolean => {
    if (this._isStorageAvailableCache !== undefined) {
      return this._isStorageAvailableCache;
    }

    if (!this.dataStorage) {
      this._isStorageAvailableCache = false;
      return false;
    }

    const testKey = "test";
    try {
      this.dataStorage.setItem(testKey, "test");
      this.dataStorage.removeItem(testKey);
    } catch {
      this._isStorageAvailableCache = false;
      return false;
    }

    this._isStorageAvailableCache = true;
    return true;
  };

  readonly getItem = (key: string): string | undefined => {
    if (this.isStorageAvailable()) {
      try {
        const value = this.dataStorage.getItem(key);
        if (typeof value === "string") {
          return value;
        }
      } catch {
        this._isStorageAvailableCache = false;
      }
    }

    if (this._fallbackCache[key] !== undefined) {
      return this._fallbackCache[key];
    }
    return undefined;
  };

  readonly setItem = (key: string, value: string): void => {
    if (this.isStorageAvailable()) {
      try {
        this.dataStorage.setItem(key, value);
        return;
      } catch {
        this._isStorageAvailableCache = false;
      }
    }

    this._fallbackCache[key] = value;
  };

  readonly removeItem = (key: string): void => {
    if (this.isStorageAvailable()) {
      try {
        this.dataStorage.removeItem(key);
        return;
      } catch {
        this._isStorageAvailableCache = false;
      }
    }

    delete this._fallbackCache[key];
  };

  readonly clear = () => {
    if (this.isStorageAvailable()) {
      try {
        this.dataStorage.clear();
        return;
      } catch {
        this._isStorageAvailableCache = false;
      }
    }

    this._fallbackCache = {};
  };
}

export class LocalStorage extends Storage {
  static get = singletonGetter(LocalStorage);
  constructor() {
    super(getLocalStorage());
  }
}

export class SessionStorage extends Storage {
  static get = singletonGetter(SessionStorage);
  constructor() {
    super(getSessionStorage());
  }
}

/**
 * @deprecated Use Storage.local. This const is evaluated too early in some test/SSR contexts.
 */
export const StorageService = new LocalStorage();
